'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { initCounterscale, trackEvent, trackPageview } from './counterscale';

// Signed-in page-view beacon → /api/me/visit (see that route for the why).
// Fires once per pathname once the client session resolves as authenticated,
// so a hard load (session fetched after mount) and a soft App Router
// navigation both count exactly once. sendBeacon is queued by the browser
// and survives unload, unlike Counterscale's XHR.
function sendVisit(path: string) {
  try {
    const body = new Blob([JSON.stringify({ path })], {
      type: 'application/json',
    });
    if (!navigator.sendBeacon?.('/api/me/visit', body)) {
      void fetch('/api/me/visit', {
        method: 'POST',
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* best-effort */
  }
}

export function AnalyticsProvider() {
  const pathname = usePathname();
  const { status } = useSession();
  const lastVisit = useRef<string | null>(null);

  useEffect(() => {
    initCounterscale();
  }, []);

  useEffect(() => {
    if (!pathname) return;
    trackPageview(pathname);
    // /play/* attribution: the /play resolver is a server redirect() (no
    // client render, no beacon), so it tags its target with ?src= and the
    // landing page reports the arrival here. share = a dated
    // namethecar.com/<date> link from a game share text; play = the bare
    // /play entry (Discord digest footer, bookmark). window.location.search
    // rather than useSearchParams() — the latter would force a Suspense
    // boundary around the root-layout mount.
    const src = new URLSearchParams(window.location.search).get('src');
    if (src === 'share') trackEvent('share_arrival');
    else if (src === 'play') trackEvent('play_arrival');
  }, [pathname]);

  useEffect(() => {
    if (status !== 'authenticated' || !pathname) return;
    if (process.env.NODE_ENV !== 'production') return;
    if (lastVisit.current === pathname) return;
    lastVisit.current = pathname;
    sendVisit(pathname);
  }, [status, pathname]);

  return null;
}
